import type { AudioEngine } from './AudioEngine.js';

export class ImpactSound {
  private lastPlayedAt = 0;
  /** Persistent gain nodes that all one-shots feed through so the Settings →
   *  Sounds slider and per-layer mute affect impact bursts the same way they
   *  affect the looped layers. Without these, one-shots routed straight into
   *  audio.master couldn't be silenced or scaled per-layer. */
  private volumeGain: GainNode;
  private muteGain: GainNode;
  private volume = 1.0;
  private muted = false;

  constructor(private audio: AudioEngine, private noiseBuf: AudioBuffer) {
    this.volumeGain = audio.ctx.createGain();
    this.volumeGain.gain.value = this.volume;
    this.muteGain = audio.ctx.createGain();
    this.muteGain.gain.value = 1.0;
    this.volumeGain.connect(this.muteGain).connect(audio.master);
  }

  setVolume(v: number): void {
    this.volume = clamp01(v);
    this.volumeGain.gain.setTargetAtTime(this.volume, this.audio.ctx.currentTime, 0.05);
  }

  getVolume(): number {
    return this.volume;
  }

  setMuted(m: boolean): void {
    this.muted = m;
    this.muteGain.gain.setTargetAtTime(m ? 0 : 1, this.audio.ctx.currentTime, 0.05);
  }

  toggleMuted(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  isMuted(): boolean {
    return this.muted;
  }

  /** Velocity in m/s. Below ~2 m/s nothing plays. */
  play(velocity: number): void {
    const v = clamp01((velocity - 2) / 15);
    if (v <= 0) return;

    const ctx = this.audio.ctx;
    const now = ctx.currentTime;

    // Debounce: don't stack multiple impacts within 80ms (typical for a single crash
    // generating several contact-start events as the chassis settles).
    if (now - this.lastPlayedAt < 0.08) return;
    this.lastPlayedAt = now;

    // Body: filtered noise burst with downward sweep
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    const offset = Math.random() * Math.max(0, this.noiseBuf.duration - 0.5);

    const lpf = ctx.createBiquadFilter();
    lpf.type = 'lowpass';
    lpf.frequency.setValueAtTime(4000, now);
    lpf.frequency.exponentialRampToValueAtTime(200, now + 0.35);
    lpf.Q.value = 1.0;

    const env = ctx.createGain();
    const peak = 0.4 + v * 0.5;
    env.gain.setValueAtTime(0, now);
    env.gain.linearRampToValueAtTime(peak, now + 0.005);
    env.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);

    src.connect(lpf).connect(env).connect(this.volumeGain);
    src.start(now, offset, 0.5);
    src.stop(now + 0.45);

    // Metallic transient for harder hits
    if (v > 0.3) {
      const click = ctx.createOscillator();
      click.type = 'square';
      click.frequency.value = 700 + Math.random() * 400;
      const clickEnv = ctx.createGain();
      clickEnv.gain.setValueAtTime(0.15 * v, now);
      clickEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);
      click.connect(clickEnv).connect(this.volumeGain);
      click.start(now);
      click.stop(now + 0.1);
    }
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
